import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Mail, Phone, MapPin, Eye, CheckCircle2, XCircle, RotateCcw, Clock, Download, ZoomIn,
  MessageSquare, Printer, Lock, Loader2, FileText, AlertTriangle, PauseCircle, PlayCircle, Unlock, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { buildRegistrationCopyPdf, buildHostelCopyPdf } from "@/lib/registrationCopy";
import { DOCUMENT_TYPES, POLICY_ITEMS, EDUCATION_BOARDS, CANDIDATE_FIELD_ITEMS, registrationIdLabel } from "@/lib/constants";
import { getAdminRegistration, updateRegistrationStatus, unfreezeApplication, downloadCandidateFiles, fetchAdminSignedPolicyDocument, syncPolicySignStatus } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";

const QUICK_REASONS = [
  "Document is blurry / unreadable",
  "Wrong document uploaded",
  "Signature / seal missing",
  "Details do not match application",
  "Document expired or invalid",
];

const FIELD_QUICK_REASONS = [
  "Photo doesn't meet requirements",
  "Photo unclear / face not visible",
];

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [reuploadFor, setReuploadFor] = useState(null);
  const [reuploadReason, setReuploadReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectConfirm, setRejectConfirm] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approvePending, setApprovePending] = useState([]);
  const [unfreezeOpen, setUnfreezeOpen] = useState(false);
  const [unfreezeReason, setUnfreezeReason] = useState("");
  const [unfreezing, setUnfreezing] = useState(false);
  const [fetchingSigned, setFetchingSigned] = useState({});
  const signedFetchAttempted = useRef(new Set());
  const [nameEditOpen, setNameEditOpen] = useState(false);
  const [nameEditValue, setNameEditValue] = useState("");
  const [nameEditSaving, setNameEditSaving] = useState(false);
  // Which status action is currently in flight (e.g. "verified", "rejected",
  // "on_hold", "submitted") — null when idle. Drives disabling all quick
  // actions during the request and showing a spinner on the one that was
  // actually clicked, instead of the old optimistic instant-flip (which
  // could show "Approved" before the write had actually finished).
  const [statusSaving, setStatusSaving] = useState(null);
  const [rejectSaving, setRejectSaving] = useState(false);
  const [withdrawSaving, setWithdrawSaving] = useState(false);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    const data = await getAdminRegistration(id);
    setApp(data || null);
    if (!silent) setLoading(false);
  };

  // Poll in the background so a student's reupload shows up live — previously
  // the admin had to leave and come back into this same application to see it.
  useEffect(() => {
    signedFetchAttempted.current = new Set();
    load();
    const iv = setInterval(() => load(true), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Roadmin's work is done at 'roadmin_cleared' — everything they controlled is now frozen.
  // Saadmin still finishes policy verification until final 'verified'.
  // On Hold parks a submitted student (not available at slot) until RO releases them.
  const isOnHold = app?.status === "on_hold";
  // roadmin_cleared kept in the locked-set for backward-compat with pre-existing
  // applications parked there by the old two-phase flow — new roadmin approvals
  // go straight to 'verified' now (see setAppStatus) and never land here.
  const roadminLocked = ["roadmin_cleared", "verified", "rejected", "withdrawn"].includes(app?.status || "");
  const isWithdrawn = app?.status === "withdrawn";
  // saadminLocked/registrarCleared/saadminReady are no longer used for gating
  // (saadmin's standalone phase is disabled) — kept commented for reinstatement.
  // const saadminLocked = ["verified", "rejected"].includes(app?.status || "");
  const isLocked = ["verified", "rejected"].includes(app?.status || "");
  const skipPolicies = !!app?.skip_policies;
  const { session } = useAdminAuth();
  const role = session?.role || "";
  const isSuperadmin = role === "superadmin";
  const isRoadmin = role === "roadmin" || isSuperadmin;
  const isAdmissions = role === "admissions";
  const canViewDocs = isRoadmin || isAdmissions;
  // saadmin login is disabled (see backend ADMIN_ACCOUNTS) — this is only ever
  // true via isSuperadmin now. Kept (not deleted) since a few display-only
  // branches below still reference it; uncomment `role === "saadmin" ||` and
  // the saadmin backend account together to reinstate a standalone session.
  const isSaadmin = /* role === "saadmin" || */ isSuperadmin;

  // Finance/Check-in each have their own narrow, list-only page — no access
  // to application detail.
  useEffect(() => {
    if (role === "finance") nav("/admin/finance", { replace: true });
    if (role === "checkin") nav("/admin/checkin", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);
  // const registrarCleared = ["roadmin_cleared", "verified"].includes(app?.status || "");
  // const saadminReady = isSaadmin && registrarCleared && !saadminLocked;
  // Finance gate: roadmin can't approve/reject/hold/withdraw until Finance has
  // cleared tuition fee. Superadmin is exempt (same bypass every other
  // roadmin-only restriction on this page already gets).
  const tuitionFeeCleared = !!app?.tuition_fee_cleared;
  const financeGatePending = isRoadmin && !isSuperadmin && !tuitionFeeCleared;
  const canManageApp = isRoadmin && !roadminLocked && !isOnHold && (isSuperadmin || tuitionFeeCleared);
  // Withdrawal is allowed even from an already-approved (verified) application —
  // deliberately independent of roadminLocked/canManageApp, which lock down once
  // verified. Still gated by the tuition-fee finance check and never available
  // once already withdrawn or rejected.
  const canWithdraw = isRoadmin && !isWithdrawn && app?.status !== "rejected" && (isSuperadmin || tuitionFeeCleared);
  const canPutOnHold = isRoadmin && app?.status === "submitted" && app?.token_number != null;
  const canReleaseHold = isRoadmin && isOnHold;
  // Roadmin now owns both documents AND policies (saadmin's separate policy
  // domain is disabled) — same gate for both categories.
  // Old role-split version:
  // const canActOnDoc = (category) => category === "policy" ? saadminReady : isRoadmin && !roadminLocked && !isOnHold;
  const canActOnDoc = () => isRoadmin && !roadminLocked && !isOnHold;
  // "Original Received" only applies to physical documents, not digitally signed policies.
  const canToggleOriginal = (category) => category === "document" && canActOnDoc(category);

  const applySignedFileMeta = (policyId, data) => {
    setApp((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        policies: {
          ...(prev.policies || {}),
          [policyId]: {
            ...(prev.policies?.[policyId] || {}),
            file_url: data.file_url,
            name: data.name || `${policyId}-signed.pdf`,
            signStatus: data.signStatus || "signed",
            signedAt: data.signedAt || prev.policies?.[policyId]?.signedAt,
          },
        },
      };
    });
  };

  const fetchSignedDoc = async (policyId, { force = false, silent = false } = {}) => {
    if (!app?.application_number) return false;
    setFetchingSigned((s) => ({ ...s, [policyId]: true }));
    try {
      const meta = app.policies?.[policyId] || {};
      if (meta.signStatus === "pending" || (!meta.signStatus && meta.sent)) {
        await syncPolicySignStatus(app.application_number, policyId);
      }
      const res = await fetchAdminSignedPolicyDocument(app.application_number, policyId, { force });
      if (res.ok && res.data?.file_url) {
        applySignedFileMeta(policyId, res.data);
        if (!silent) toast.success("Signed document ready");
        return true;
      }
      const detail = res.data?.detail || "Could not fetch signed document from Leegality";
      if (!silent) toast.error(typeof detail === "string" ? detail : "Could not fetch signed document");
      return false;
    } catch {
      if (!silent) toast.error("Could not fetch signed document from Leegality");
      return false;
    } finally {
      setFetchingSigned((s) => ({ ...s, [policyId]: false }));
    }
  };

  // For signed Leegality policies missing a cached PDF, pull from Leegality once
  // so ROadmin Preview/Download work without a manual refresh.
  useEffect(() => {
    if (!app || !isRoadmin || skipPolicies) return;
    const missing = POLICY_ITEMS.filter((p) => {
      if (p.viewOnly) return false;
      const meta = app.policies?.[p.id] || {};
      const attemptKey = `${app.application_number}:${p.id}`;
      return (
        meta.signStatus === "signed"
        && meta.leegality_document_id
        && !meta.file_url
        && !signedFetchAttempted.current.has(attemptKey)
      );
    });
    if (missing.length === 0) return;
    missing.forEach((p) => {
      signedFetchAttempted.current.add(`${app.application_number}:${p.id}`);
      fetchSignedDoc(p.id, { silent: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, isRoadmin]);

  // Build a unified document list: DOCUMENT_TYPES + policies, then filter by role.
  // roadmin → only regular documents. saadmin → only policies/forms. Super Admin
  // → roadmin now sees and acts on both (saadmin's separate policy-only view
  // is disabled), so everything lives under one isRoadmin-gated block.
  const docList = useMemo(() => {
    if (!app) return [];
    const out = [];
    if (canViewDocs) {
      const cand = app.candidate || {};
      // "name" is deliberately excluded here — it's edited directly from the
      // candidate summary card (see btn-edit-name) instead of living in this
      // table. Only fields with an actual file (photograph) belong here.
      CANDIDATE_FIELD_ITEMS.filter((f) => f.type === "photo").forEach((f) => {
        const photoUrl = app.photograph?.file_url || cand.photo_url;
        out.push({
          uniqueKey: `field:${f.id}`,
          docKey: f.id,
          category: "field",
          label: f.label,
          fileName: app.photograph?.name || "Photograph",
          fileUrl: photoUrl || null,
          status: app.doc_statuses?.[f.id] || "submitted",
        });
      });
      DOCUMENT_TYPES.forEach((d) => {
        const files = app.documents?.[d.id] || [];
        if (files.length > 0) {
          files.forEach((f, idx) => {
            out.push({
              uniqueKey: `doc:${d.id}:${idx}`,
              docKey: d.id,
              category: "document",
              label: files.length > 1 ? `${d.label} (${idx + 1})` : d.label,
              fileName: f.name,
              fileUrl: f.file_url,
              status: app.doc_statuses?.[d.id] || "submitted",
            });
          });
        }
      });
      POLICY_ITEMS.forEach((p) => {
        if (skipPolicies) return;
        const f = app.policies?.[p.id];
        // Policies have no candidate file upload — show once sent for sign.
        // When Leegality signing is complete, file_url holds the cached signed PDF.
        if (f?.sent) {
          out.push({
            uniqueKey: `pol:${p.id}`,
            docKey: p.id,
            category: "policy",
            label: p.label,
            fileName: f.name || (f.file_url ? `${p.id}-signed.pdf` : null),
            fileUrl: f.file_url || (p.viewOnly ? p.pdfUrl : null) || null,
            status: app.doc_statuses?.[p.id] || "submitted",
          });
        }
      });
    }
    return out;
  }, [app, canViewDocs, skipPolicies]);

  // Digitally signed policies for the Digital signs tab (roadmin view).
  const digitalSigns = useMemo(() => {
    if (!app || skipPolicies) return [];
    return POLICY_ITEMS.map((p) => {
      const meta = app.policies?.[p.id] || {};
      const signStatus = meta.signStatus || (meta.sent ? "pending" : null);
      const fileUrl = meta.file_url || (p.viewOnly ? p.pdfUrl : null) || null;
      return {
        id: p.id,
        label: p.label,
        description: p.description,
        signers: p.signers,
        viewOnly: !!p.viewOnly,
        sent: !!meta.sent,
        signStatus,
        sentAt: meta.sentAt || null,
        signedAt: meta.signedAt || null,
        fileUrl,
        fileName: meta.name || (p.viewOnly ? "anti-drug-policy.pdf" : null),
      };
    }).filter((p) => p.sent || p.signStatus);
  }, [app]);

  // Mandatory items still flagged for reupload — approving despite these means
  // the candidate never fixed something required. Each role's confirmation is
  // scoped to their own domain: roadmin's approve checks documents; saadmin's
  // final approve checks policies. Documents aren't re-checked at saadmin's
  // step since roadmin already had their own confirmation opportunity for them.
  const pendingMandatoryDocs = useMemo(() => {
    if (!app) return [];
    const isNonIndian = app.candidate?.nationality && app.candidate.nationality !== "Indian";
    return DOCUMENT_TYPES.filter((d) => {
      const required = d.id === "nationality" ? isNonIndian : d.required;
      return required && app.doc_statuses?.[d.id] === "reupload";
    });
  }, [app]);
  const pendingPolicyReuploads = useMemo(() => {
    if (!app || skipPolicies) return [];
    return POLICY_ITEMS.filter((p) => app.doc_statuses?.[p.id] === "reupload");
  }, [app, skipPolicies]);

  const requestApprove = () => {
    // saadmin's separate final-approval phase is disabled — roadmin's single
    // approval now covers both documents and policies, so check both pending
    // reupload sets together instead of splitting by role.
    // Old role-split version:
    // const pending = isSaadmin ? pendingPolicyReuploads : pendingMandatoryDocs;
    const pending = [...pendingMandatoryDocs, ...pendingPolicyReuploads];
    if (pending.length > 0) {
      setApprovePending(pending.map((x) => x.label));
      setApproveConfirmOpen(true);
      return;
    }
    setAppStatus("verified");
  };

  const confirmApproveAnyway = () => {
    setApproveConfirmOpen(false);
    setAppStatus("verified");
  };

  const setAppStatus = async (s) => {
    // Roadmin (or Super Admin acting as roadmin) now handles the whole approval
    // alone — straight to verified, no more separate saadmin final-approval phase.
    // Allow roadmin to park submitted apps as on_hold and release them back to submitted.
    // Old two-phase version (isSaadminFinal let saadmin seal roadmin_cleared → verified):
    // const isSaadminFinal = isSaadmin && s === "verified" && registrarCleared && !saadminLocked;
    const isHoldAction =
      (canPutOnHold && s === "on_hold") || (canReleaseHold && s === "submitted");
    if (!canManageApp && !isHoldAction) {
      return toast.error(
        isOnHold
          ? "Release On Hold before changing status"
          : "Only Reception (roadmin) can change the application status"
      );
    }
    if (roadminLocked) {
      toast.error("Application is locked — no further changes");
      return;
    }
    const prevStatus = app.status;
    const actingAs = "roadmin";
    setStatusSaving(s);
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: actingAs, status: s });
    setStatusSaving(null);
    if (res) {
      setApp(res);
      const msg =
        s === "on_hold"
          ? "Application put On Hold"
          : prevStatus === "on_hold" && s === "submitted"
            ? "On Hold released"
            : `Application marked as ${s === "verified" ? "Approved" : s}`;
      toast.success(msg);
    } else {
      toast.error("Failed to update status");
    }
  };

  const setDocStatus = async (docKey, category, s, label) => {
    if (!canActOnDoc(category))
      return toast.error("Only Reception (roadmin) can verify documents & policies");
    if (roadminLocked) return toast.error("Application is locked — no further changes");
    const prevStatus = app.doc_statuses?.[docKey];
    const actingAs = "roadmin";
    setApp((prev) => ({ ...prev, doc_statuses: { ...prev.doc_statuses, [docKey]: s } }));
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: actingAs, doc_statuses: { [docKey]: s }, doc_label: label || docKey });
    if (res) {
      setApp(res);
      toast.success(`${label || docKey} · ${s}`);
    } else {
      setApp((prev) => ({ ...prev, doc_statuses: { ...prev.doc_statuses, [docKey]: prevStatus } }));
      toast.error("Failed to update — please try again");
    }
  };

  const toggleOriginalReceived = async (docKey, category) => {
    if (roadminLocked) return;
    if (!canToggleOriginal(category)) return;
    const current = !!app.original_received?.[docKey];
    const next = !current;
    setApp((prev) => ({ ...prev, original_received: { ...prev.original_received, [docKey]: next } }));
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: "roadmin", original_received: { [docKey]: next } });
    if (res) setApp(res);
    else {
      setApp((prev) => ({ ...prev, original_received: { ...prev.original_received, [docKey]: current } }));
      toast.error("Failed to update — please try again");
    }
  };

  const submitReupload = async () => {
    if (reuploadFor?.scope === "app" && !canManageApp)
      return toast.error("Only Reception (roadmin) can request reupload of the whole application");
    if (reuploadFor?.scope === "doc" && !canActOnDoc(reuploadFor.category))
      return toast.error("Only Reception (roadmin) can request reupload of documents & policies");
    const reason = reuploadReason.trim();
    if (reason.length < 5) return toast.error("Please enter a reason (at least 5 characters)");
    if (reuploadFor.scope === "app") {
      const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: "roadmin", status: "reupload", reason });
      if (res) {
        setApp(res);
        toast.success("Reupload requested with reason");
      }
    } else {
      const res = await updateRegistrationStatus(id, { actor_role: role,
        acting_as: "roadmin",
        doc_statuses: { [reuploadFor.docKey]: "reupload" },
        doc_label: reuploadFor.label,
        reason,
      });
      if (res) {
        setApp(res);
        toast.success(`${reuploadFor.label} · reupload requested`);
      }
    }
    setReuploadFor(null);
    setReuploadReason("");
  };

  const submitReject = async () => {
    if (!canManageApp) return toast.error("Only Reception (roadmin) can reject the application");
    const reason = rejectReason.trim();
    if (reason.length < 5) return toast.error("Please enter a rejection reason (at least 5 characters)");
    if (!rejectConfirm) return toast.error("Please tick the confirmation checkbox to proceed");
    setRejectSaving(true);
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: "roadmin", status: "rejected", reason });
    setRejectSaving(false);
    if (res) {
      setApp(res);
      toast.success("Application rejected");
    }
    setRejectOpen(false);
    setRejectReason("");
    setRejectConfirm(false);
  };

  const submitWithdraw = async () => {
    if (!canWithdraw) return toast.error("Only Reception (roadmin) can withdraw the application");
    const reason = withdrawReason.trim();
    if (reason.length < 5) return toast.error("Please enter a reason (at least 5 characters)");
    if (!withdrawConfirm) return toast.error("Please tick the confirmation checkbox to proceed");
    setWithdrawSaving(true);
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: "roadmin", status: "withdrawn", reason });
    setWithdrawSaving(false);
    if (res) {
      setApp(res);
      toast.success("Application marked as withdrawn");
    }
    setWithdrawOpen(false);
    setWithdrawReason("");
    setWithdrawConfirm(false);
  };

  // Admin edits the candidate's name directly instead of requesting a
  // reupload/resubmit round-trip — there's nothing candidate-only about
  // fixing a typo'd name, so this saves a full back-and-forth cycle.
  const submitNameEdit = async () => {
    if (!canActOnDoc()) return toast.error("Only Reception (roadmin) can edit the candidate name");
    const newName = nameEditValue.trim();
    if (newName.length < 3) return toast.error("Enter a valid name (at least 3 characters)");
    setNameEditSaving(true);
    const res = await updateRegistrationStatus(id, { actor_role: role, acting_as: "roadmin", candidate_name: newName });
    setNameEditSaving(false);
    if (res) {
      setApp(res);
      toast.success("Candidate name updated");
      setNameEditOpen(false);
    } else {
      toast.error("Could not save — please try again");
    }
  };

  const submitUnfreeze = async () => {
    if (!isSuperadmin) return toast.error("Only Super Admin can unfreeze an application");
    setUnfreezing(true);
    const res = await unfreezeApplication(id, { actor_role: "superadmin", reason: unfreezeReason.trim() || undefined });
    setUnfreezing(false);
    if (res.ok) {
      setApp(res.data);
      toast.success("Application unfrozen — back to submitted");
      setUnfreezeOpen(false);
      setUnfreezeReason("");
    } else {
      toast.error(res.data?.detail || "Could not unfreeze — please try again");
    }
  };

  const printCopies = async () => {
    // Open the tab synchronously (still inside the click handler) so Chrome
    // doesn't block it as a "popup without user gesture" later.
    const win = window.open("about:blank", "_blank");
    try {
      toast.loading("Preparing Student & Office copies…", { id: "copies-pdf" });
      const { url, fileName } = await buildRegistrationCopyPdf(app);
      if (win) {
        win.location.href = url;
        setTimeout(() => { try { win.focus(); win.print(); } catch (_e) { /* ignore */ } }, 900);
      } else {
        // Popup blocked — fall back to a download.
        const a = document.createElement("a");
        a.href = url; a.download = fileName; a.click();
      }
      toast.success("Student & Office copies ready", { id: "copies-pdf" });
    } catch (_e) {
      try { win && win.close(); } catch (_ignored) { /* noop */ }
      toast.error("Could not generate copies", { id: "copies-pdf" });
    }
  };

  // Separate print action from the Student/Office copy — only relevant for
  // hostelers, and an admin may need to reprint just this one on its own.
  const printHostelCopy = async () => {
    const win = window.open("about:blank", "_blank");
    try {
      toast.loading("Preparing Hostel copy…", { id: "hostel-copy-pdf" });
      // Refetch so a bed reserved after page load is reflected on the slip.
      const fresh = await getAdminRegistration(id);
      const { url, fileName } = await buildHostelCopyPdf(fresh || app);
      if (win) {
        win.location.href = url;
        setTimeout(() => { try { win.focus(); win.print(); } catch (_e) { /* ignore */ } }, 900);
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = fileName; a.click();
      }
      toast.success("Hostel copy ready", { id: "hostel-copy-pdf" });
    } catch (_e) {
      try { win && win.close(); } catch (_ignored) { /* noop */ }
      toast.error("Could not generate hostel copy", { id: "hostel-copy-pdf" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-[#F7F7F7]">
        <DashboardSidebar variant="admin" />
        <main className="flex-1 p-10 flex items-center justify-center text-[#6B7280]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading application…
        </main>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-screen flex bg-[#F7F7F7]">
        <DashboardSidebar variant="admin" />
        <main className="flex-1 p-10">
          <button onClick={() => nav(-1)} className="mu-btn-ghost -ml-3">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="mu-card p-10 text-center mt-6">
            <div className="text-lg font-semibold text-[#111]">Application not found</div>
            <p className="text-sm text-[#6B7280] mt-1">The application you are looking for does not exist.</p>
            <Link to={isAdmissions ? "/admin/applications" : "/admin/registrations"} className="mu-btn-primary mt-4 inline-flex">Back to list</Link>
          </div>
        </main>
      </div>
    );
  }

  const cand = app.candidate || {};
  const photoUrl = app.photograph?.file_url || cand.photo_url || `https://ui-avatars.com/api/?background=B60F2A&color=fff&name=${encodeURIComponent(cand.name || "N A")}`;

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">
        <button onClick={() => nav(-1)} className="mu-btn-ghost -ml-3 text-sm" data-testid="btn-back-admin-detail">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 mt-4">
          {/* Left: Candidate */}
          <div className="space-y-4">
            <div className="mu-card p-6" data-testid="candidate-summary">
              <div className="flex flex-col items-center text-center">
                <img src={photoUrl} alt={cand.name} className="h-24 w-24 rounded-2xl object-cover border border-[#E5E7EB]" />
                <div className="mt-4 flex items-center gap-1.5">
                  <h2 className="text-xl font-bold text-[#111]">{cand.name || "—"}</h2>
                  {isRoadmin && (
                    <button
                      type="button"
                      className="p-1 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      title={canActOnDoc() ? "Edit name" : "roadmin only"}
                      onClick={() => { setNameEditValue(cand.name || ""); setNameEditOpen(true); }}
                      disabled={roadminLocked || !canActOnDoc()}
                      data-testid="btn-edit-name"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-[#6B7280]">{app.application_number}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">Roll · {app.roll_number || "—"}</p>
                <div className="mt-3"><StatusBadge status={app.status} /></div>
              </div>
              <div className="mt-6 space-y-3 text-sm">
                {cand.email && <div className="flex items-center gap-2 text-[#6B7280]"><Mail className="h-4 w-4" /> {cand.email}</div>}
                {app.mobile && <div className="flex items-center gap-2 text-[#6B7280]"><Phone className="h-4 w-4" /> {app.mobile}</div>}
                {cand.nationality && <div className="flex items-center gap-2 text-[#6B7280]"><MapPin className="h-4 w-4" /> {cand.nationality}</div>}
              </div>
            </div>

            <div className="mu-card p-6">
              <div className="tiny-label">Quick actions</div>
              <div className="mt-4 grid grid-cols-1 gap-2">
                {isAdmissions && (
                  <div className="flex items-center gap-2 text-xs text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 mb-1" data-testid="banner-admissions-view-only">
                    <Eye className="h-3.5 w-3.5" />
                    View only — you can preview and download uploaded documents. No changes can be made from this login.
                  </div>
                )}
                {!isRoadmin && !isAdmissions && (
                  <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-1">
                    <Lock className="h-3.5 w-3.5" />
                    Sign in as Registrar Office (roadmin) to approve/reject the application.
                  </div>
                )}
                {/* saadmin's separate "waiting for registrar" message — disabled along with the saadmin login.
                {isSaadmin && !canManageApp && (
                  <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-1">
                    <Lock className="h-3.5 w-3.5" />
                    {registrarCleared
                      ? "Registrar Office has approved. Verify all 4 policies, then click Approve application below."
                      : "Waiting for Registrar Office to approve the application before Student Affairs can act."}
                  </div>
                )}
                */}
                {isLocked && (
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-1">
                    <Lock className="h-3.5 w-3.5" /> Approved · locked from further edits
                  </div>
                )}
                {isWithdrawn && (
                  <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-1">
                    <Lock className="h-3.5 w-3.5" /> Application withdrawn · locked from further edits
                  </div>
                )}
                {financeGatePending && !roadminLocked && !isOnHold && (
                  <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-1" data-testid="banner-finance-pending">
                    <Lock className="h-3.5 w-3.5" /> Finance clearance pending — tuition fee not cleared. Contact the Finance office before processing this application.
                  </div>
                )}
                {isOnHold && isRoadmin && (
                  <div className="flex items-center gap-2 text-xs text-slate-700 bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 mb-1">
                    <PauseCircle className="h-3.5 w-3.5" /> On Hold — student not available at slot. Release to continue.
                  </div>
                )}
                {/* Legacy-data only: an application parked at the old intermediate
                    'roadmin_cleared' status from before saadmin was disabled — new
                    approvals never land here anymore (see setAppStatus). */}
                {roadminLocked && !isLocked && !isWithdrawn && isRoadmin && (
                  <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-1">
                    <Lock className="h-3.5 w-3.5" /> Parked at the old two-phase &ldquo;Registrar approved&rdquo; status — ask a Super Admin to Unfreeze it, then re-approve.
                  </div>
                )}
                {/* Roadmin actions — approve / reupload / reject the application */}
                {isRoadmin && (
                  <>
                    <button className="mu-btn-primary" onClick={requestApprove} data-testid="btn-approve-app" disabled={!canManageApp || !!statusSaving}>
                      {statusSaving === "verified" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {statusSaving === "verified" ? "Approving…" : "Approve application"}
                    </button>
                    <button className="mu-btn-secondary" onClick={() => { setReuploadReason(""); setReuploadFor({ scope: "app" }); }} data-testid="btn-reupload-app" disabled={!canManageApp || !!statusSaving}>
                      <RotateCcw className="h-4 w-4" /> Request reupload
                    </button>
                    <button className="mu-btn-secondary text-red-700 hover:bg-red-50" onClick={() => { setRejectReason(""); setRejectConfirm(false); setRejectOpen(true); }} data-testid="btn-reject-app" disabled={!canManageApp || !!statusSaving}>
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                    <button className="mu-btn-secondary text-red-700 hover:bg-red-50" onClick={() => { setWithdrawReason(""); setWithdrawConfirm(false); setWithdrawOpen(true); }} data-testid="btn-withdraw-app" disabled={!canWithdraw || !!statusSaving}>
                      <XCircle className="h-4 w-4" /> Mark Withdrawn
                    </button>
                    {canPutOnHold && (
                      <button
                        className="mu-btn-secondary text-slate-700 hover:bg-slate-50"
                        onClick={() => setAppStatus("on_hold")}
                        data-testid="btn-on-hold-app"
                        disabled={!!statusSaving}
                      >
                        {statusSaving === "on_hold" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PauseCircle className="h-4 w-4" />}
                        {statusSaving === "on_hold" ? "Saving…" : "Put On Hold"}
                      </button>
                    )}
                    {canReleaseHold && (
                      <button
                        className="mu-btn-primary"
                        onClick={() => setAppStatus("submitted")}
                        data-testid="btn-release-hold-app"
                        disabled={!!statusSaving}
                      >
                        {statusSaving === "submitted" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                        {statusSaving === "submitted" ? "Saving…" : "Release On Hold"}
                      </button>
                    )}
                  </>
                )}
                {/* Saadmin final approval — disabled along with the saadmin login;
                    roadmin's own "Approve application" button above now completes
                    the whole approval in one step.
                {isSaadmin && (
                  <button
                    className="mu-btn-primary"
                    onClick={requestApprove}
                    data-testid="btn-saadmin-final-approve"
                    disabled={!registrarCleared || saadminLocked}
                    title={!registrarCleared ? "Waiting for Registrar Office approval" : ""}
                  >
                    <CheckCircle2 className="h-4 w-4" /> Approve application
                  </button>
                )}
                */}
                {isRoadmin && roadminLocked && app?.status !== "rejected" && !isWithdrawn && (
                  <button className="mu-btn-primary mt-2" onClick={printCopies} data-testid="btn-print-copies">
                    <Printer className="h-4 w-4" />
                    Print Student & Office Copy
                  </button>
                )}
                {isRoadmin && roadminLocked && app?.status !== "rejected" && !isWithdrawn && (app?.hostel?.is_hosteler || app?.hostel?.hostel_status === "reserved") && (
                  <button className="mu-btn-secondary mt-2" onClick={printHostelCopy} data-testid="btn-print-hostel-copy">
                    <Printer className="h-4 w-4" />
                    Print Hostel Copy
                  </button>
                )}
                {canViewDocs && (
                  <button
                    type="button"
                    className="mu-btn-secondary mt-2"
                    data-testid="btn-download-candidate-files"
                    onClick={async () => {
                      const res = await downloadCandidateFiles(app.application_number);
                      if (!res.ok) {
                        toast.error(res.data?.detail || "Download failed — please sign in again");
                      }
                    }}
                  >
                    <Download className="h-4 w-4" /> Download documents (ZIP)
                  </button>
                )}
                {isSuperadmin && ["roadmin_cleared", "verified", "rejected", "on_hold", "withdrawn"].includes(app?.status) && (
                  <button
                    className="mu-btn-secondary text-amber-800 hover:bg-amber-50 mt-2"
                    onClick={() => { setUnfreezeReason(""); setUnfreezeOpen(true); }}
                    data-testid="btn-unfreeze-app"
                  >
                    <Unlock className="h-4 w-4" /> Unfreeze application
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right: Tabs */}
          <div className="mu-card p-6">
            <Tabs defaultValue="documents" className="w-full">
              <TabsList className="bg-[#F7F7F7]" data-testid="tabs-list">
                <TabsTrigger value="documents" data-testid="tab-documents">Documents & Policies</TabsTrigger>
                {isRoadmin && !skipPolicies && (
                  <TabsTrigger value="digital-signs" data-testid="tab-digital-signs">Digital signs</TabsTrigger>
                )}
                <TabsTrigger value="details" data-testid="tab-details">Candidate Details</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="documents" className="mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold">Document</th>
                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                        <th className="text-center px-4 py-3 font-semibold">Original Received</th>
                        <th className="text-right px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {docList.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-8 text-[#9CA3AF]">No documents uploaded yet.</td></tr>
                      )}
                      {docList.map((d) => {
                        const received = !!app.original_received?.[d.docKey];
                        const rowCanAct = canActOnDoc(d.category);
                        const rowCanTick = canToggleOriginal(d.category);
                        const rowLocked = roadminLocked;
                        const otherRoleLabel = "roadmin only";
                        return (
                          <tr key={d.uniqueKey} data-testid={`doc-row-${d.docKey}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => d.fileUrl && setPreview({ name: d.label, url: d.fileUrl })}
                                  disabled={!d.fileUrl}
                                  className={`h-10 w-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center overflow-hidden transition group relative shrink-0 ${d.fileUrl ? "hover:border-[#B60F2A] cursor-pointer" : "cursor-default"}`}
                                  aria-label={d.fileUrl ? `Preview ${d.label}` : d.label}
                                  data-testid={`doc-thumb-${d.docKey}`}
                                >
                                  {d.fileUrl && /\.(png|jpe?g|webp|gif)$/i.test(d.fileName || "") ? (
                                    <img src={d.fileUrl} alt={d.label} className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <FileText className="h-4 w-4 text-[#B60F2A]" />
                                  )}
                                  {d.fileUrl && (
                                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition">
                                      <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition" />
                                    </span>
                                  )}
                                </button>
                                <div className="min-w-0">
                                  <div className="text-[#111] font-medium truncate max-w-[260px] flex items-center gap-2">
                                    {d.label}
                                    {/* "Waiting for Registrar" badge — disabled along with the
                                        saadmin sequential gate; roadmin owns policies directly now.
                                    {d.category === "policy" && isSaadmin && !registrarCleared && (
                                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
                                        <Lock className="h-2.5 w-2.5" /> Waiting for Registrar
                                      </span>
                                    )}
                                    */}
                                  </div>
                                  <div className="text-[10px] text-[#9CA3AF] truncate max-w-[260px]">
                                    {d.category === "policy"
                                      ? (d.fileUrl ? (d.fileName || "Signed PDF from Leegality") : "Sent for digital sign — awaiting signed PDF")
                                      : d.fileName}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                            <td className="px-4 py-3 text-center">
                              {d.category !== "document" ? (
                                <span className="text-[#D1D5DB]">—</span>
                              ) : (
                                <Checkbox
                                  checked={received}
                                  disabled={rowLocked || !rowCanTick}
                                  onCheckedChange={() => toggleOriginalReceived(d.docKey, d.category)}
                                  className="data-[state=checked]:bg-[#B60F2A] data-[state=checked]:border-[#B60F2A] mx-auto"
                                  data-testid={`chk-original-${d.docKey}`}
                                />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F7F7F7] disabled:opacity-30" title={d.fileUrl ? "Preview" : "No file — sent for sign only"} onClick={() => d.fileUrl && setPreview({ name: d.label, url: d.fileUrl })} disabled={!d.fileUrl} data-testid={`btn-view-doc-${d.docKey}`}>
                                  <Eye className="h-4 w-4" />
                                </button>
                                {d.fileUrl && (
                                  <a
                                    href={d.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F7F7F7]"
                                    title="Open / download"
                                    data-testid={`btn-download-doc-${d.docKey}`}
                                  >
                                    <Download className="h-4 w-4" />
                                  </a>
                                )}
                                {!isAdmissions && (
                                  <>
                                <button className="p-2 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-30" title={rowCanAct ? "Approve" : otherRoleLabel} onClick={() => setDocStatus(d.docKey, d.category, "verified", d.label)} disabled={rowLocked || !rowCanAct} data-testid={`btn-approve-doc-${d.docKey}`}>
                                  <CheckCircle2 className="h-4 w-4" />
                                </button>
                                <button className="p-2 rounded-lg text-orange-700 hover:bg-orange-50 disabled:opacity-30" title={rowCanAct ? "Request reupload" : otherRoleLabel} onClick={() => { setReuploadReason(""); setReuploadFor({ scope: "doc", docKey: d.docKey, category: d.category, label: d.label }); }} disabled={rowLocked || !rowCanAct}>
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                                <button className="p-2 rounded-lg text-red-700 hover:bg-red-50 disabled:opacity-30" title={rowCanAct ? "Reject" : otherRoleLabel} onClick={() => setDocStatus(d.docKey, d.category, "rejected", d.label)} disabled={rowLocked || !rowCanAct}>
                                  <XCircle className="h-4 w-4" />
                                </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {isRoadmin && !skipPolicies && (
                <TabsContent value="digital-signs" className="mt-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">Policy</th>
                          <th className="text-left px-4 py-3 font-semibold">Sign status</th>
                          <th className="text-left px-4 py-3 font-semibold">Signed on</th>
                          <th className="text-right px-4 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {digitalSigns.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center py-8 text-[#9CA3AF]">
                              No digitally signed policies yet.
                            </td>
                          </tr>
                        )}
                        {digitalSigns.map((p) => {
                          const isSigned = p.signStatus === "signed";
                          const isPending = p.signStatus === "pending";
                          const isFetching = !!fetchingSigned[p.id];
                          const canFetchFromLeegality = !p.viewOnly && (isSigned || isPending) && !!app?.policies?.[p.id]?.leegality_document_id;
                          return (
                            <tr key={p.id} data-testid={`digital-sign-row-${p.id}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center shrink-0">
                                    <FileText className="h-4 w-4 text-[#B60F2A]" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[#111] font-medium truncate max-w-[280px]">{p.label}</div>
                                    <div className="text-[10px] text-[#9CA3AF] truncate max-w-[280px]">
                                      {p.viewOnly ? "Acknowledged (view-only)" : `To sign: ${p.signers}`}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {isSigned ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Signed
                                  </span>
                                ) : isPending ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 px-2 py-1 rounded-full">
                                    <Clock className="h-3.5 w-3.5" /> Pending
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[#6B7280] bg-[#F7F7F7] ring-1 ring-inset ring-[#E5E7EB] px-2 py-1 rounded-full">
                                    Sent
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-[#6B7280]">
                                {p.signedAt
                                  ? new Date(p.signedAt).toLocaleString()
                                  : p.sentAt
                                    ? `Sent ${new Date(p.sentAt).toLocaleString()}`
                                    : "—"}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  {canFetchFromLeegality && !p.fileUrl && (
                                    <button
                                      className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F7F7F7] disabled:opacity-30"
                                      title={isFetching ? "Fetching from Leegality…" : "Fetch signed PDF from Leegality"}
                                      onClick={() => fetchSignedDoc(p.id, { force: true })}
                                      disabled={isFetching}
                                      data-testid={`btn-fetch-digital-sign-${p.id}`}
                                    >
                                      {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                                    </button>
                                  )}
                                  <button
                                    className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F7F7F7] disabled:opacity-30"
                                    title={p.fileUrl ? "Preview" : isFetching ? "Fetching signed PDF…" : "No file available to preview"}
                                    onClick={() => p.fileUrl && setPreview({ name: p.label, url: p.fileUrl })}
                                    disabled={!p.fileUrl}
                                    data-testid={`btn-view-digital-sign-${p.id}`}
                                  >
                                    {isFetching && !p.fileUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                  {p.fileUrl && (
                                    <a
                                      href={p.fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      download={p.fileName || p.label}
                                      className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F7F7F7]"
                                      title="Download"
                                      data-testid={`btn-download-digital-sign-${p.id}`}
                                    >
                                      <Download className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="details" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    [registrationIdLabel(skipPolicies), app.application_number],
                    ["ACK Number", app.ack_number],
                    ["Roll Number", app.roll_number],
                    ["Full Name", cand.name],
                    ["Email", cand.email],
                    ["Mobile", app.mobile],
                    ["Nationality", cand.nationality],
                    ["Date of Birth", cand.date_of_birth],
                    ["Qualifying Board (12th)", EDUCATION_BOARDS.find((b) => b.id === cand.education_board)?.label || cand.education_board],
                    ["Blood Group", cand.blood_group],
                    ["Gender", cand.gender],
                    ["Parent 1", cand.parent1_name],
                    ["Parent 1 Phone", cand.parent1_phone],
                    ["Parent 1 Relation", cand.parent1_relation],
                    ["Parent 2", cand.parent2_name],
                    ["Parent 2 Phone", cand.parent2_phone],
                    ["Parent 2 Relation", cand.parent2_relation],
                    ["Address", [cand.address, cand.city, cand.state, cand.pincode].filter(Boolean).join(", ")],
                    ["Anti-Ragging Ref.", app.affidavit_ref],
                    ["Submitted On", app.submitted_at ? new Date(app.submitted_at).toLocaleString() : "—"],
                    ["Reporting Slot", app.assigned_slot ? `${app.assigned_slot.dateLabel} · ${app.assigned_slot.timeLabel}` : "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="p-4 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/60">
                      <div className="tiny-label">{k}</div>
                      <div className="text-sm font-medium text-[#111] mt-1 break-all">{v || "—"}</div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-6">
                  {(app.history || []).length === 0 && <li className="text-sm text-[#9CA3AF] ml-6">No activity yet.</li>}
                  {(app.history || []).map((h, i) => (
                    <li key={i} className="ml-6" data-testid={`history-${i}`}>
                      <span className="absolute -left-1.5 flex items-center justify-center w-3 h-3 rounded-full bg-[#B60F2A] ring-4 ring-[#FBE7EA]" />
                      <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(h.at).toLocaleString()}
                      </div>
                      <div className="text-sm font-medium text-[#111] mt-1">{h.event}</div>
                      {h.reason && <div className="text-xs text-[#6B7280]">Reason: {h.reason}</div>}
                    </li>
                  ))}
                </ol>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Edit candidate name dialog — admin types the fix directly instead of
            requesting a reupload and waiting for the candidate to retype it. */}
        <Dialog open={nameEditOpen} onOpenChange={setNameEditOpen}>
          <DialogContent data-testid="name-edit-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Pencil className="h-5 w-5 text-blue-700" />
                </div>
                <DialogTitle>Edit candidate name</DialogTitle>
              </div>
              <DialogDescription>This updates the application directly — no reupload request, no waiting on the candidate.</DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <label className="tiny-label" htmlFor="name-edit-input">Candidate name</label>
              <input
                id="name-edit-input"
                className="mu-input mt-1"
                value={nameEditValue}
                onChange={(e) => setNameEditValue(e.target.value.toUpperCase())}
                data-testid="name-edit-input"
                autoFocus
              />
            </div>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setNameEditOpen(false)} data-testid="btn-cancel-name-edit">Cancel</button>
              <button className="mu-btn-primary" onClick={submitNameEdit} disabled={nameEditSaving} data-testid="btn-save-name-edit">
                {nameEditSaving ? "Saving…" : (<><CheckCircle2 className="h-4 w-4" /> Save</>)}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject confirmation dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-700" />
                </div>
                <DialogTitle>Reject application</DialogTitle>
              </div>
              <DialogDescription>
                This will mark the application as <span className="font-semibold text-red-700">rejected</span>. The candidate will be notified with the reason below.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <label className="tiny-label" htmlFor="reject-reason">Reason for rejection</label>
              <Textarea id="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Documents don't match application details." rows={4} className="mt-1" data-testid="reject-reason-input" />
              <label className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer">
                <Checkbox checked={rejectConfirm} onCheckedChange={(v) => setRejectConfirm(!!v)} data-testid="reject-confirm-checkbox" className="mt-0.5 data-[state=checked]:bg-[#B60F2A] data-[state=checked]:border-[#B60F2A]" />
                <span className="text-sm text-[#111]">
                  I confirm and want to <span className="font-semibold">proceed</span> with rejecting this application.
                </span>
              </label>
            </div>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setRejectOpen(false)} disabled={rejectSaving} data-testid="btn-cancel-reject">Cancel</button>
              <button className="mu-btn-primary bg-red-700 hover:bg-red-800" onClick={submitReject} disabled={rejectSaving} data-testid="btn-confirm-reject">
                {rejectSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                {rejectSaving ? "Rejecting…" : "Proceed with reject"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Withdrawn confirmation dialog */}
        <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
          <DialogContent data-testid="withdraw-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-700" />
                </div>
                <DialogTitle>Mark application withdrawn</DialogTitle>
              </div>
              <DialogDescription>
                This will mark the application as <span className="font-semibold text-red-700">withdrawn</span> — e.g. the candidate has decided not to join. This locks the application from further edits.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <label className="tiny-label" htmlFor="withdraw-reason">Reason for withdrawal</label>
              <Textarea id="withdraw-reason" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} placeholder="e.g. Candidate confirmed they are joining another institution." rows={4} className="mt-1" data-testid="withdraw-reason-input" />
              <label className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer">
                <Checkbox checked={withdrawConfirm} onCheckedChange={(v) => setWithdrawConfirm(!!v)} data-testid="withdraw-confirm-checkbox" className="mt-0.5 data-[state=checked]:bg-[#B60F2A] data-[state=checked]:border-[#B60F2A]" />
                <span className="text-sm text-[#111]">
                  I confirm and want to <span className="font-semibold">proceed</span> with marking this application withdrawn.
                </span>
              </label>
            </div>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setWithdrawOpen(false)} disabled={withdrawSaving} data-testid="btn-cancel-withdraw">Cancel</button>
              <button className="mu-btn-primary bg-red-700 hover:bg-red-800" onClick={submitWithdraw} disabled={withdrawSaving} data-testid="btn-confirm-withdraw">
                {withdrawSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                {withdrawSaving ? "Withdrawing…" : "Proceed with withdrawal"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unfreeze confirmation dialog — Super Admin only */}
        <Dialog open={unfreezeOpen} onOpenChange={setUnfreezeOpen}>
          <DialogContent data-testid="unfreeze-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Unlock className="h-5 w-5 text-amber-700" />
                </div>
                <DialogTitle>Unfreeze application</DialogTitle>
              </div>
              <DialogDescription>
                This reverts the application from <span className="font-semibold">{app?.status}</span> back to <span className="font-semibold text-amber-700">submitted</span>, unlocking normal roadmin/saadmin actions again. If this application was already pushed to the university's student records (student_details/users), those records will be deleted — they'll be recreated fresh the next time it's genuinely approved.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <label className="tiny-label" htmlFor="unfreeze-reason">Reason (optional)</label>
              <Textarea id="unfreeze-reason" value={unfreezeReason} onChange={(e) => setUnfreezeReason(e.target.value)} placeholder="e.g. Approved by mistake." rows={3} className="mt-1" data-testid="unfreeze-reason-input" />
            </div>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setUnfreezeOpen(false)} data-testid="btn-cancel-unfreeze">Cancel</button>
              <button className="mu-btn-primary bg-amber-700 hover:bg-amber-800" onClick={submitUnfreeze} disabled={unfreezing} data-testid="btn-confirm-unfreeze">
                <Unlock className="h-4 w-4" /> {unfreezing ? "Unfreezing…" : "Unfreeze"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve-despite-pending-reupload confirmation dialog */}
        <Dialog open={approveConfirmOpen} onOpenChange={setApproveConfirmOpen}>
          <DialogContent data-testid="approve-confirm-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-700" />
                </div>
                <DialogTitle>Mandatory items still pending</DialogTitle>
              </div>
              <DialogDescription>
                The candidate has not reuploaded the following mandatory item{approvePending.length > 1 ? "s" : ""} yet:
              </DialogDescription>
            </DialogHeader>
            <ul className="list-disc list-inside text-sm text-[#111] space-y-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              {approvePending.map((label) => <li key={label}>{label}</li>)}
            </ul>
            <p className="text-sm text-[#6B7280] mt-2">Do you still want to approve this application?</p>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setApproveConfirmOpen(false)} data-testid="btn-cancel-approve-confirm">Cancel</button>
              <button className="mu-btn-primary" onClick={confirmApproveAnyway} data-testid="btn-confirm-approve-anyway">
                <CheckCircle2 className="h-4 w-4" /> Yes, approve anyway
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reupload dialog */}
        <Dialog open={!!reuploadFor} onOpenChange={(v) => !v && setReuploadFor(null)}>
          <DialogContent data-testid="reupload-reason-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <RotateCcw className="h-5 w-5 text-orange-700" />
                </div>
                <DialogTitle>
                  {reuploadFor?.scope === "app" ? "Request reupload — entire application" : `Request reupload — ${reuploadFor?.label || "document"}`}
                </DialogTitle>
              </div>
              <DialogDescription>The candidate will be notified with this reason. Please be specific so they can fix it quickly.</DialogDescription>
            </DialogHeader>

            <div className="mt-2">
              <label className="tiny-label" htmlFor="reupload-reason">Reason for reupload</label>
              <Textarea id="reupload-reason" value={reuploadReason} onChange={(e) => setReuploadReason(e.target.value)} placeholder="e.g. The scan is blurry — please upload a clearer copy." rows={4} className="mt-1" data-testid="reupload-reason-input" />
              <div className="text-xs text-[#9CA3AF] mt-1">{reuploadReason.length}/300 characters</div>
              <div className="mt-3">
                <div className="tiny-label mb-2">Quick reasons</div>
                <div className="flex flex-wrap gap-2">
                  {(reuploadFor?.category === "field" ? FIELD_QUICK_REASONS : QUICK_REASONS).map((r) => (
                    <button key={r} type="button" onClick={() => setReuploadReason(r)} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB] bg-white hover:border-[#111] hover:text-[#111] text-[#6B7280] transition" data-testid={`quick-reason-${r.slice(0, 12)}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <button type="button" className="mu-btn-secondary" onClick={() => setReuploadFor(null)} data-testid="btn-cancel-reupload">Cancel</button>
              <button type="button" className="mu-btn-primary" onClick={submitReupload} data-testid="btn-confirm-reupload">
                <MessageSquare className="h-4 w-4" /> Send request
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document preview dialog */}
        <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="max-w-3xl" data-testid="admin-doc-preview">
            <DialogHeader>
              <DialogTitle className="truncate pr-6">{preview?.name}</DialogTitle>
              <DialogDescription>Uploaded by {cand.name} · {app.application_number}</DialogDescription>
            </DialogHeader>
            <div className="mt-2 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7] overflow-hidden">
              {preview?.url && /\.(png|jpe?g|webp|gif)$/i.test(preview.url) ? (
                <img src={preview.url} alt={preview.name} className="w-full max-h-[65vh] object-contain bg-white" />
              ) : (
                <iframe src={preview?.url} title={preview?.name} className="w-full h-[65vh] bg-white" />
              )}
            </div>
            <div className="flex items-center justify-end mt-3">
              <div className="flex gap-2">
                <a href={preview?.url} target="_blank" rel="noreferrer" className="mu-btn-secondary text-sm">
                  <Eye className="h-4 w-4" /> Open full-size
                </a>
                <a href={preview?.url} download={preview?.name} className="mu-btn-primary text-sm">
                  <Download className="h-4 w-4" /> Download
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
